/**
 * AOI clip — spec §G6 ("AOI clip & swipe (mutually exclusive)"), whose reference
 * implementation is copied below essentially verbatim:
 *
 * ```ts
 * function updateAoiClip(map: L.Map, aoi: LatLng[]|null, on: boolean) {
 *   const panes = ['rasterPane','choroPane'].map(p => map.getPane(p)!);
 *   if (!on || !aoi) { panes.forEach(p => p.style.clipPath = ''); return; }
 *   const poly = aoi.map(ll => { const p = map.latLngToContainerPoint(ll); return `${p.x}px ${p.y}px`; }).join(',');
 *   panes.forEach(p => p.style.clipPath = `polygon(${poly})`);
 * }
 * map.on('move zoom viewreset', () => updateAoiClip(map, aoi, aoiClip));
 * ```
 *
 * The clip is expressed in **container pixels**, so it has to be recomputed every time the
 * view moves — hence the `move zoom viewreset` subscription §G6 spells out. `move` (not
 * `moveend`) is what keeps the mask glued to the ground mid-pan.
 *
 * `rasterPane` and `choroPane` are the *thematic* panes only: §F5.10's clip masks the data,
 * never the basemap or the §F5.8 boundaries, so the reader can still see where the AOI sits.
 *
 * Ownership: this module and `Swipe.tsx` both write `clip-path` on the same two panes, and
 * §G6 makes them mutually exclusive in the store (`setAoiClip`/`setSwipe`). Each effect
 * early-returns when its own mode is off, so exactly one of them ever registers a cleanup
 * and the hand-off (React runs every destroy in a commit before any create) cannot leave a
 * stale mask behind.
 */
import { useEffect } from 'react';
import type L from 'leaflet';

import { useAppStore } from '../../lib/state';
import type { LatLon } from '../../lib/geo';

/** §G6: "`clip-path` on rasterPane+choroPane". */
export const CLIP_PANES = ['rasterPane', 'choroPane'] as const;

/** Write one `clip-path` value (or `''` to release) to both thematic panes. */
export function setPaneClip(map: L.Map, value: string): void {
  for (const name of CLIP_PANES) {
    const pane = map.getPane(name);
    if (pane) pane.style.clipPath = value;
  }
}

/** §G6, verbatim: mask the thematic panes to the AOI ring in current container pixels. */
export function updateAoiClip(map: L.Map, aoi: readonly LatLon[] | null, on: boolean): void {
  if (!on || !aoi || aoi.length < 3) {
    setPaneClip(map, '');
    return;
  }
  const poly = aoi
    .map((ll) => {
      const p = map.latLngToContainerPoint(ll);
      return `${p.x}px ${p.y}px`;
    })
    .join(',');
  setPaneClip(map, `polygon(${poly})`);
}

/**
 * Keep the §F1 `aoi`/`aoiClip` pair applied to the map, re-projecting on every view change.
 *
 * Called once, from `MapTools`. Nothing renders — the whole effect is two inline styles.
 */
export function useAoiClip(map: L.Map): void {
  const aoi = useAppStore((s) => s.aoi);
  const aoiClip = useAppStore((s) => s.aoiClip);

  useEffect(() => {
    if (!aoiClip || !aoi) return;
    const pts = aoi.pts;
    const apply = () => updateAoiClip(map, pts, true);
    apply();
    // §G6 names `move zoom viewreset`; `resize` is added because the clip is expressed in
    // **container** pixels and `invalidateSize` (MapView's ResizeObserver — collapsing the
    // sidebar is 332 px) moves every one of them without firing any of the other three.
    // Without it the mask stays where the AOI used to be on screen.
    map.on('move zoom viewreset resize', apply);
    return () => {
      map.off('move zoom viewreset resize', apply);
      setPaneClip(map, '');
    };
  }, [map, aoi, aoiClip]);
}
